/**
 * RetakeConfirmModal
 *
 * Single retake-confirmation surface for the heroes with a result to
 * retake (CheckUp, Care, QultureUp). Replaces the standalone
 * `HeroRetakeModal` (Radix `AlertDialog`) — see the 2026-04-28 UX
 * review §B-2 for the full rationale.
 *
 * The wrapper composes the existing `ConfirmModal` chassis with:
 *   • `intent='retake'`     — amber/RefreshCw visual taxonomy
 *   • `useRetakeGuide()`    — atomic server mutation (archive prior
 *                              result + reset guide-state in one tx)
 *   • per-hero copy: a title and a body per flow (no hero noun is put
 *     into a sentence — pt, es and it agree gender and articles)
 *   • Keep / Lose visualizer — promotes the consequence-clarity that
 *                              ConfirmModal already does for Restart /
 *                              Discard. Retake archives the prior
 *                              result; the user keeps it (in history)
 *                              but loses their active score / badge.
 *   • Arming animation      — same 1s sweep as Restart / Discard so
 *                              the safety bar is uniform across the
 *                              whole lifecycle suite.
 *
 * Usage:
 *
 *   const [open, setOpen] = useState(false);
 *
 *   <Button onClick={() => setOpen(true)}>Retake</Button>
 *   <RetakeConfirmModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     flowId="checkup"
 *     journeyInstanceId={journeyId}
 *     onAfterRetake={() => navigate('/checkup/assessment')}
 *   />
 */

import { useRetakeGuide } from '@tonnie/sdk';
import { useState } from 'react';

import { ConfirmModal } from './lifecycle-modals';
import type { ConfirmModalConfig, FlowId, KeepLoseItem } from './types';

interface RetakeConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: RetakeFlowId;
  journeyInstanceId: string | undefined;
  /**
   * Items the user keeps after retake. Defaults to "your previous
   * result is archived" — hosts can extend (e.g. QultureUp also
   * keeps mission progress, Passport miles, etc.).
   */
  keep?: KeepLoseItem[];
  /**
   * Items the user loses. Defaults to "your active score & badge."
   */
  lose?: KeepLoseItem[];
  /**
   * Called AFTER the retake mutation succeeds. Typical use: navigate
   * the user to the assessment URL.
   */
  onAfterRetake: () => void;
}

const DEFAULT_KEEP: KeepLoseItem[] = [
  {
    labelKey: 'copilot.lifecycle.retake.keep.archived',
  },
];

const DEFAULT_LOSE: KeepLoseItem[] = [
  {
    labelKey: 'copilot.lifecycle.retake.lose.activeScore',
  },
];

/** The flows with a result to retake — TelePortal's setup restarts, the builder has none. */
export type RetakeFlowId = Extract<FlowId, 'checkup' | 'care_assessment' | 'qultureup'>;

/** Each flow's own title and body — a sentence per flow, never a noun put into one. */
const RETAKE_COPY_BY_FLOW: Record<RetakeFlowId, { titleKey: string; bodyKey: string }> = {
  checkup: {
    titleKey: 'copilot.lifecycle.retake.titleCheckup',
    bodyKey: 'copilot.lifecycle.retake.bodyCheckup',
  },
  care_assessment: {
    titleKey: 'copilot.lifecycle.retake.titleCare',
    bodyKey: 'copilot.lifecycle.retake.bodyCare',
  },
  qultureup: {
    titleKey: 'copilot.lifecycle.retake.titleQultureup',
    bodyKey: 'copilot.lifecycle.retake.bodyQultureup',
  },
};

export function RetakeConfirmModal({
  open,
  onOpenChange,
  flowId,
  journeyInstanceId,
  keep,
  lose,
  onAfterRetake,
}: RetakeConfirmModalProps) {
  const retakeMutation = useRetakeGuide();
  const [submitting, setSubmitting] = useState(false);

  const config: ConfirmModalConfig = {
    ...RETAKE_COPY_BY_FLOW[flowId],
    confirmLabelKey: 'copilot.lifecycle.retake.confirm',
    cancelLabelKey: 'copilot.lifecycle.stay',
    arming: true,
    intent: 'warning',
    reversible: false,
    keep: keep ?? DEFAULT_KEEP,
    lose: lose ?? DEFAULT_LOSE,
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await retakeMutation.mutateAsync({
        flowId,
        journeyId: journeyInstanceId,
        archivePreviousResult: true,
      });
      onOpenChange(false);
      onAfterRetake();
    } catch (err) {
      // The SDK hook already invalidates caches on conflict; we keep
      // the modal open so the user can retry. Errors that aren't
      // 409-conflict bubble to the host's error boundary via the
      // mutation's onError plumbing.
      console.warn('[RetakeConfirmModal] retake failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      config={config}
      busy={submitting}
      destructive
      intent="retake"
      onConfirm={handleConfirm}
      onCancel={() => !submitting && onOpenChange(false)}
    />
  );
}
